import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom"; // Added useLocation
import QuizForm from "./QuizForm";
import { useTrainingStore } from "../../store/store";
import { useAuth } from "../../auth/AuthProvider";
import { Test, Training } from "../../types/api";
import { useTrainingModule } from "./TrainingModuleContext";

const AttemptTest: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // Test ID
  const navigate = useNavigate();
  const location = useLocation(); // To access query parameters
  const { moduleType, validCategories, paths } = useTrainingModule();
  const isFixedCategory = validCategories.length === 1;
  const { trainings, fetchTrainings, submitTest } = useTrainingStore();
  const { user } = useAuth();
  const [test, setTest] = useState<Test | null>(null);
  const [training, setTraining] = useState<Training | null>(null);
  const [answers, setAnswers] = useState<
    { questionId: number; selectedOption: number }[]
  >([]);
  const [loading, setLoading] = useState(true);

  // Get traineeId from query parameter or user.id
  const queryParams = new URLSearchParams(location.search);
  const traineeId = queryParams.get("traineeId") || user?.id;

  useEffect(() => {
    const loadTest = async () => {
      if (id && !test && !training) {
        try {
          setLoading(true);
          await fetchTrainings({
            type: "all",
            where: isFixedCategory
              ? { moduleType, category: validCategories[0] }
              : { moduleType },
          });
          const updatedTrainings = useTrainingStore
            .getState()
            .trainings.filter(
              (t) =>
                t?.moduleType === moduleType ||
                (isFixedCategory && t?.category === validCategories[0]),
            );
          const trainingData = updatedTrainings.find((t) =>
            t.tests?.some((test) => test.id === id),
          );
          if (trainingData) {
            setTraining(trainingData);
            const relevantTest = trainingData.tests?.find(
              (t: Test) => t.id === id,
            );
            setTest(relevantTest || null);
          }
          setLoading(false);
        } catch (error) {
          console.error("Error loading test:", error);
          setLoading(false);
        }
      }
    };
    loadTest();
  }, [id]);

  const handleOptionSelect = (questionId: number, selectedOption: number) => {
    setAnswers((prev) => {
      const existingAnswer = prev.find(
        (answer) => answer.questionId === questionId,
      );
      if (existingAnswer) {
        return prev.map((answer) =>
          answer.questionId === questionId
            ? { ...answer, selectedOption }
            : answer,
        );
      }
      return [...prev, { questionId, selectedOption }];
    });
  };

  const handleSubmit = async () => {
    if (!traineeId) {
      console.error("No trainee ID provided");
      alert("Cannot submit test: No trainee selected.");
      return;
    }
    if (id && training) {
      try {
        const scoreData = await submitTest(id, answers, traineeId);
        const trainee = training.trainees.find(
          (t) => t.traineeId === traineeId,
        );
        if (!trainee) {
          alert("Cannot submit test: Trainee not found in training.");
          return;
        }
        const preTestScore = trainee.results?.preTestScore ?? "N/A";
        const postTestScore = trainee.results?.postTestScore ?? "N/A";
        // alert(
        //   `Test submitted successfully! ${
        //     trainee.name
        //   }'s ${test?.testCategory || ""} score: ${scoreData.score}%\nPre-Test Score: ${preTestScore}%\nPost-Test Score: ${postTestScore}%`
        // );
        navigate(paths.dashboard);
      } catch (error) {
        console.error("Submission error:", error);
        alert(
          `Failed to submit test: ${
            typeof error === "object" && error !== null && "message" in error
              ? (error as { message?: string }).message
              : "Please try again."
          }`,
        );
      }
    } else {
      console.error("Missing data:", { id, traineeId, training });
      alert("Cannot submit test: Missing required data.");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!test || !training) {
    return <div>Test or training not found.</div>;
  }

  return (
    <div className="px-2 py-4 space-y-6 mt-14">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold  text-blue-800">Start Test</h1>
        <div className="flex justify-center items-center space-x-8">
          <div className="text-center">
            <p className="font-semibold">Topic</p>
            <p>{training.topic}</p>
          </div>
          <div className="text-center">
            <p className="font-semibold">Category</p>
            <p>{test.testCategory}</p>
          </div>
          <div className="text-center">
            <p className="font-semibold">Date</p>
            <p>{new Date(test.date).toLocaleDateString()}</p>
          </div>
          <button
            onClick={() => navigate(paths.dashboard)}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Back
          </button>
        </div>
      </div>
      <div className="bg-white p-4 rounded shadow">
        <QuizForm
          questions={test.quiz || []}
          mode="view"
          onOptionSelect={handleOptionSelect}
          selectedAnswers={answers}
        />
        <div className="flex bg-[#61748E] justify-end gap-4 px-2 py-2 mt-6">
          <button
            onClick={handleSubmit}
            className=" bg-[#0fa9a6] text-white hover:bg-green-600 px-10 py-2 rounded-full"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttemptTest;
